/* eslint-disable @typescript-eslint/no-unsafe-argument */
'use client';

import { cn } from '@/shared/shadcn/lib/utils';
import { Control, FieldValues, FormProviderProps } from 'react-hook-form';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/shared/shadcn/ui/form';
import { Checkbox } from '@/shared/shadcn/ui/checkbox';
import { Switch } from '@/shared/shadcn/ui/switch';
import { Input } from '@/shared/shadcn/ui/input';
import { ChangeEvent, FormEvent, useState } from 'react';
import { Select, SelectContent, SelectTrigger, SelectValue } from '@/shared/shadcn/ui/select';
import { Textarea } from '@/shared/shadcn/ui/textarea';
import Image from 'next/image';
import { Message } from '@/shared/typography/message';
import { ImageUp } from 'lucide-react';
import { MultiSelect } from '@/shared/ui/multi-select';
import { DateTimePicker } from '@/shared/ui/date-time-picker';
import fileService from '@/legacy/shared/api/file.service';
import { Button } from '@/shared/shadcn/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/shadcn/ui/popover';
import dynamic from 'next/dynamic';

const Wysiwyg = dynamic(() => import('@/widgets/wysiwig/Wysiwyg').then((mod) => mod.Wysiwyg), { ssr: false });

export interface FormedFieldProps {
  control: Control<any>;
  name: string;
  label?: string;
  className?: string;
  description?: string;
}

export function SuperForm<
  TFieldValues extends FieldValues,
  TContext = any,
  TTransformedValues extends FieldValues | undefined = undefined,
>({
  children,
  onSubmit,
  ...props
}: FormProviderProps<TFieldValues, TContext, TTransformedValues> & {
  onSubmit?: (event: FormEvent<HTMLFormElement>) => Promise<void>;
  className?: string;
}) {
  return (
    <Form {...props}>
      <form
        onSubmit={(event) => {
          onSubmit?.(event);
        }}
        className={cn(props.className, 'flex flex-col gap-6')}
      >
        {children}
      </form>
    </Form>
  );
}

export function InputSuperFormField({
  control,
  name,
  label,
  className,
  description,
  ...props
}: FormedFieldProps & React.ComponentProps<typeof Input>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        field.value = field.value === null ? '' : field.value;
        return (
          <FormItem className={cn('flex flex-col', className)}>
            {label && <FormLabel>{label}</FormLabel>}
            <FormControl>
              <Input {...props} {...field} />
            </FormControl>
            {description && <p className="text-muted-foreground text-xs">{description}</p>}
            <FormMessage className={'-mt-2'} />
          </FormItem>
        );
      }}
    />
  );
}

export function DatePickerSuperFormField({ control, name, label, className }: FormedFieldProps) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const date = field.value as Date | undefined;
        return (
          <FormItem className={cn('flex flex-col', className)}>
            {label && <FormLabel>{label}</FormLabel>}
            <DateTimePicker name={name} date={date} onDateChange={field.onChange} withClear />
            <FormMessage className={'-mt-2'} />
          </FormItem>
        );
      }}
    />
  );
}

export function SelectSuperFormField({
  control,
  name,
  label,
  className,
  description,
  placeholder,
  children,
  onValueChange,
  ...props
}: FormedFieldProps & React.ComponentProps<typeof Select> & { placeholder?: string; children?: React.ReactNode }) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const value = field.value !== undefined && field.value !== null ? String(field.value) : undefined;
        return (
          <FormItem className={cn('flex flex-col', className)}>
            {label && <FormLabel>{label}</FormLabel>}
            <Select
              {...props}
              onValueChange={(val) => {
                field.onChange(val);
                onValueChange?.(val);
              }}
              value={value}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder={placeholder} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>{children}</SelectContent>
            </Select>
            {description && <p className="text-muted-foreground text-xs">{description}</p>}
            <FormMessage className={'-mt-2'} />
          </FormItem>
        );
      }}
    />
  );
}

export function MultiSelectSuperFormField({
  control,
  name,
  label,
  options,
  placeholder,
  className,
  ...props
}: FormedFieldProps & Pick<React.ComponentProps<typeof MultiSelect>, 'options'> & { placeholder?: string }) {
  const [open, setOpen] = useState(false);

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        if (!Array.isArray(field.value)) {
          return <div />;
        }
        const values = field.value
          .map((id) => {
            const thatOption = options.find((option) => option.id === id);
            return thatOption;
          })
          .filter((value) => value !== undefined);

        return (
          <FormItem className={cn('flex flex-col', className)}>
            {label && <FormLabel>{label}</FormLabel>}
            <FormControl>
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    aria-expanded={open}
                    className="selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input
                      hover:bg-background! focus-visible:border-ring focus-visible:ring-ring/50 flex h-9 w-full min-w-0
                      justify-between overflow-hidden rounded-md border bg-transparent px-3 py-1 text-sm! font-normal!
                      shadow-xs transition-[color,box-shadow] focus-visible:ring-[3px]"
                  >
                    {values.map((value) => value.title).join(', ') || placeholder}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="h-min w-full p-0" align="start" side="bottom" avoidCollisions={false}>
                  <MultiSelect
                    {...props}
                    options={options}
                    values={values}
                    onChange={(values) => field.onChange(values.map((option) => option.id))}
                  />
                </PopoverContent>
              </Popover>
            </FormControl>
            <FormMessage className={'-mt-2'} />
          </FormItem>
        );
      }}
    />
  );
}

export function TextAreaSuperFormField({
  control,
  name,
  label,
  className,
  description,
  ...props
}: FormedFieldProps & React.ComponentProps<typeof Textarea>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={cn('flex flex-col', className)}>
          {label && <FormLabel>{label}</FormLabel>}
          <FormControl>
            <Textarea {...props} {...field} />
          </FormControl>
          {description && <p className="text-muted-foreground text-xs">{description}</p>}
          <FormMessage className={'-mt-2'} />
        </FormItem>
      )}
    />
  );
}

export function CheckboxSuperFormField({
  control,
  name,
  label,
  className,
  ...props
}: FormedFieldProps & React.ComponentProps<typeof Checkbox>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={cn('flex flex-col', className)}>
          <div className="flex items-center gap-2">
            <FormControl>
              <Checkbox {...props} checked={field.value as boolean} onCheckedChange={field.onChange} />
            </FormControl>
            {label && <FormLabel>{label}</FormLabel>}
          </div>
          <FormMessage className={'-mt-2'} />
        </FormItem>
      )}
    />
  );
}

export function SwitchSuperFormField({
  control,
  name,
  label,
  className,
  ...props
}: FormedFieldProps & React.ComponentProps<typeof Switch>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={cn('flex flex-col', className)}>
          <div className="flex items-center gap-2">
            <FormControl>
              <Switch {...props} checked={field.value as boolean} onCheckedChange={field.onChange} />
            </FormControl>
            {label && <FormLabel>{label}</FormLabel>}
          </div>
          <FormMessage className={'-mt-2'} />
        </FormItem>
      )}
    />
  );
}

export function ImageUploadSuperFormField({
  control,
  name,
  label,
  uploadType = 'file',
  folder,
  className,
  description,
}: FormedFieldProps & { uploadType?: 'file' | 'url'; folder?: string }) {
  const [imageUrl, setImageUrl] = useState('');
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const isString = typeof field.value === 'string';
        const isFile = field.value instanceof File;
        const inputId = `${name}-upload`;

        if (isFile) {
          const reader = new FileReader();
          reader.onload = (event) => {
            const result = event.target?.result;
            if (typeof result === 'string') {
              setImageUrl(result);
            }
          };
          reader.readAsDataURL(field.value);
        }

        const handleUploadFile = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
          const file = e.target.files?.[0];
          if (!file) return;
          if (uploadType === 'file') {
            field.onChange(file);
          } else {
            const url = await fileService.uploadFile(file, folder);
            field.onChange(url);
          }
        };

        return (
          <FormItem className={cn('flex flex-col overflow-hidden', className)}>
            {label && <FormLabel>{label}</FormLabel>}
            <FormLabel htmlFor={inputId} className="relative mt-1 h-48">
              <div
                className="-z-auto absolute top-0 right-0 bottom-0 left-0 flex items-center justify-center bg-black/40
                  opacity-0 hover:opacity-100"
              >
                <div className="text-background flex flex-col items-center">
                  <ImageUp className="h-16 w-16" />
                  <Message className="mt-2">Загрузить изображение</Message>
                </div>
              </div>
              {isString && (
                <Image
                  src={`${process.env.NEXT_PUBLIC_STORAGE_URL}${field.value}`}
                  width={100}
                  height={100}
                  unoptimized={true}
                  className="bg-muted h-full w-full object-contain"
                  alt=""
                />
              )}
              {isFile && imageUrl && (
                <Image
                  src={imageUrl}
                  width={100}
                  height={100}
                  className="bg-muted h-full w-full object-contain"
                  unoptimized={true}
                  alt=""
                />
              )}
              {!isFile && !isString && <div className="bg-muted h-full w-full" />}
            </FormLabel>
            <FormControl>
              <input
                id={inputId}
                type="file"
                className="hidden"
                onChange={(e) => {
                  void handleUploadFile(e);
                }}
              />
            </FormControl>
            {description && <p className="text-muted-foreground text-xs">{description}</p>}
            <FormMessage className={'-mt-2'} />
          </FormItem>
        );
      }}
    />
  );
}

export function WysiwygSuperFormField({
  control,
  name,
  label,
  className,
  siteId,
}: FormedFieldProps & { siteId?: string }) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={cn('flex flex-col', className)}>
          {label && <FormLabel>{label}</FormLabel>}
          <FormControl>
            <Wysiwyg defaultValue={field.value as string} onChange={field.onChange} siteId={siteId} />
          </FormControl>
          <FormMessage className={'-mt-2'} />
        </FormItem>
      )}
    />
  );
}
